import { amorphicStatic, property, remote, supertypeClass } from '@haventech/amorphic';
import moment from 'moment';

import { Policy } from '../../../common/js/customer/Policy';
import { Customer } from '../../../common/js/customer/Customer';
import { BaseQuote } from '../../../common/js/quote/BaseQuote';
import {
  ApplicationTypes,
  CoverpathCustomerProps,
  CPMetadataFilingTypes,
  FeatureFlags,
} from '../../js/interfaces/CoverpathTypes';
import { CoverpathTermQuote } from '../quote/coverpath/CoverpathTermQuote';
import { CoverpathWholeLifeQuote } from '../quote/coverpath/CoverpathWholeLifeQuote';
import { QuoteMap } from '../quote/QuoteClassMap';
import { Assumptions } from '../../../common/js/static/Assumptions';
import { WorkflowNames } from '../../../common/js/orca/WorkflowName';
import { PolicyAgent } from '../../../common/js/customer/PolicyAgent';
import { Auth0Utils } from '../../../common/js/authentication/Auth0Utils';
import { AppConfigTypes } from '../../../common/js/config/AppConfig';
import { AMCAuth0ResponseUser, Auth0Client } from '../../../common/js/authentication/Auth0Client';
import { Utils } from '../../../common/js/Utils';
import { CasApplication } from './CasApplication';
import { CoverpathPolicy } from './CoverpathPolicy';
import { TwoFactorAuth } from '../../../common/js/customer/TwoFactorAuth';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { CustomerActiveApplications } from '../../../common/js/customer/CustomerActiveApplications';
import { CoverpathAnnuityQuote } from '../quote/coverpath/CoverpathAnnuityQuote';
import { CoverpathDocuSign } from '../../../coverpath-common/js/docusign/CoverpathDocuSign';
import { Product } from '../../../common/js/static/Product';
import { PrimaryAgentAndPolicies } from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { LoggerSingleton, Category } from '../../../common/js/logger/LoggerSingleton';
import { Agent } from '../../../common/js/customer/Agent';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class CoverpathCustomer extends Customer {
  @property({ toServer: false })
  channel: string = 'CAS';

  @property({ toClient: false })
  _twoFactorAuth: TwoFactorAuth;

  @property({ toClient: false })
  tfaAuthenticated: boolean;

  @property({
    type: Array,
    getType: () => BaseQuote,
    comment: 'insurance quotes for a customer. one customer to many quotes',
  })
  insuranceQuotes: Array<BaseQuote> = [];

  @property({ toServer: false, getType: () => CasApplication, comment: 'All the applications for a given customer' })
  protected applications: Array<CasApplication> = [];

  get primaryPolicyAgents(): Array<PrimaryAgentAndPolicies> {
    const applications = this.applications;

    if (applications && applications.length > 0) {
      const rawPrimaryPolicyAgents: Array<Agent> = applications.map(
        (application) => application.primaryPolicy.primaryPolicyAgent.agent
      );
      const rawPrimaryPolicies: Array<Policy> = applications.map(
        (application) => application.primaryPolicy.primaryPolicyAgent.policy
      );

      // remove duplicate agents
      const primaryPolicyAgents: Array<Agent> = rawPrimaryPolicyAgents.filter(
        (agent, index, arr) => index === arr.findIndex((item) => item.producerId === agent.producerId)
      );

      const primaryAgentAndPolicies: Array<PrimaryAgentAndPolicies> = primaryPolicyAgents.map((primaryPolicyAgent) => {
        const policies: Array<Policy> = rawPrimaryPolicies.filter(
          (primaryPolicy) =>
            primaryPolicy.primaryPolicyAgent.agent.producerId === primaryPolicyAgent.producerId &&
            !!primaryPolicy.policyNumber
        );

        return {
          agent: primaryPolicyAgent,
          policies: policies,
        };
      });
      return primaryAgentAndPolicies;
    }
    return [];
  }

  get application(): CasApplication {
    return this.getApplication();
  }

  set application(application: CasApplication) {
    this.setApplication(application);
  }

  getApplication(): CasApplication {
    if (this._currentApplication) {
      return this._currentApplication as CasApplication;
    } else if (this.activeApplications.length === 1) {
      return this.activeApplications[0] as CasApplication;
    } else if (this.activeApplications.length > 1) {
      const error = {
        isHumanRelated: false,
        data: { data: this._id },
      };
      UILoggerSingleton.getInstance().error({
        module: 'Customer',
        function: 'get application',
        category: UICategory.request,
        error: error,
      });
      return null;
      //may be redirect the customer to dashboard view
    }
  }

  setApplication(application: CasApplication) {
    this._currentApplication = application;
    if (!this.applications.includes(application as CasApplication)) {
      this.applications.push(application as CasApplication);
      this.applications = this.applications.filter((app) => app.type === ApplicationTypes.ACTIVE);
    }
  }

  /**
   * Adding a newly created application to the customer without marking it as the _currentApplication
   */
  addApplication(application: CasApplication): void {
    if (!this.applications.includes(application)) {
      this.applications.push(application);
      // semotus doesn't keep track of changes using Array.push so needs to be reassigned.
      this.applications = this.applications;
    }
  }

  @property({ toClient: true })
  tfaRequired: boolean;

  @property({ toServer: false, comment: 'Indicates if the customer has registered an account' })
  hasRegistered: boolean;

  static new(quoteType: string) {
    const customer: CoverpathCustomer = new CoverpathCustomer().init();
    customer.initializeQuote(quoteType);
    return customer;
  }

  static create = function (props: CoverpathCustomerProps) {
    const setPolicyProps = () => {
      customer.application.primaryPolicy.ownerPersonResidentialAddress.state = props.address.state;
      customer.application.isAgentAssistant = props.isAgentAssistant || false;
      return customer;
    };

    const customer = CoverpathCustomer.new(props.quoteType);

    customer.reset();
    customer.updateProps(props);

    return customer.createPolicy().then(setPolicyProps);
  };

  retrieveQuoteSpec() {
    return {
      insuranceQuotes: true,
    };
  }

  fetchQuotes() {
    const idMap = {};

    idMap[this._id] = this;

    return this.fetch(this.retrieveQuoteSpec(), null, idMap);
  }

  get termQuote(): CoverpathTermQuote {
    if (this.application && this.application.quote && this.application.quote.productType === 'Term') {
      return this.application.quote as CoverpathTermQuote;
    } else if (this.application && this.application.quote && this.application.quote.secondaryQuote) {
      return this.application.quote.secondaryQuote as CoverpathTermQuote;
    }
  }

  get wholeLifeQuote(): CoverpathWholeLifeQuote {
    if (this.application && this.application.quote && this.application.quote.productType === 'Whole') {
      return this.application.quote as CoverpathWholeLifeQuote;
    }
  }

  get annuityQuote(): CoverpathAnnuityQuote {
    if (this.application && this.application.quote && this.application.quote.productType === 'Annuity') {
      return this.application.quote as CoverpathAnnuityQuote;
    }
  }

  get clientQuote(): BaseQuote {
    const clientQuote = this.insuranceQuotes.find((quote) => {
      return (
        !quote.isPrior && quote.amorphicGetClassName() === CoverpathTermQuote.amorphicClassName && !quote.isAdvisorQuote
      );
    });
    return clientQuote ? (clientQuote as CoverpathTermQuote) : null;
  }

  get currentQuote(): BaseQuote {
    return this.clientQuote || this.primaryQuote;
  }

  get advisorQuote(): BaseQuote {
    if (this.application && this.application.quote && this.application.quote.isAdvisorQuote) {
      return this.application.quote;
    }
  }

  /* returns the quote for the primary policy. a joint app primary policy is whole life */
  get primaryQuote(): BaseQuote {
    if (this.clientQuote) {
      return this.clientQuote;
    } else if (this.application && this.application.quote) {
      return this.application.quote;
    }
  }

  /**
   * Get the active applications for this customer.
   * An active application does not have its type populated with any values.
   * A non-active application could have its type populated as 'prior' or 'deleted'
   */
  get activeApplications(): Array<CasApplication> {
    return this.applications;
  }

  /**
   * use-case:
   * When all applications have been deleted, we show a help page with advisor contact info,
   * this function allows for showing contact info of advisor who "recently interacted with the customer"
   */
  getRecentlyCreatedApplication(): CasApplication {
    return this.applications.reduce(
      (recentApp, app) => (recentApp && moment(recentApp.createdTime).isAfter(app.createdTime) ? recentApp : app),
      this.application
    );
  }

  get firstNameLastName(): string {
    const firstNameLastName: string = `${this.firstName ? this.firstName : ''}${
      this.lastName ? ' ' + this.lastName : ''
    }`;
    return firstNameLastName.trim();
  }

  /**
   * we may not know which type of quote we want to attach to the customer at compile time,
   * so use this method to figure out which type of quote we want to instantiate given
   * a string key to a map of string -> quote classes.
   * @param {Customer} customer
   * @param {string} type
   * @returns {BaseQuote}
   */
  static getDynamicQuoteObj(customer: Customer, type: string): BaseQuote {
    return QuoteMap[type].new(customer);
  }

  updateProps(props: CoverpathCustomerProps, sendApp?: boolean) {
    this.email = props.person.email;
    this.firstName = props.person.firstName;
    this.lastName = props.person.lastName;
    if (!sendApp) {
      //only override these values with agent quick quote if initially creating app
      this.primaryCustomer.person.firstName = this.firstName;
      this.primaryCustomer.person.lastName = this.lastName;
      this.primaryCustomer.address.state = props.address.state;
      this.primaryCustomer.person.age = props.person.age;

      //and only override primaryCustomer email if owner is same as insured
      if (
        !this.application ||
        !this.application.primaryPolicy ||
        (this.application.primaryPolicy && !this.application.primaryPolicy.insured)
      ) {
        this.primaryCustomer.person.email = this.email;
      }
    }
    this.advanceApplicationProgress(props.progress || 10);
    this.tfaRequired = props.tfaRequired === true;
    if (!!props.primaryQuote) {
      props.primaryQuote.customer = this;
      if (this.primaryQuote.hasBeenInitialized()) {
        for (const quote of this.insuranceQuotes) {
          quote.isPrior = true;
        }
        props.primaryQuote.isPrior = false;
        this.insuranceQuotes.unshift(props.primaryQuote);
      } else {
        this.insuranceQuotes = [props.primaryQuote];
      }
      if (props.termQuote) {
        props.termQuote.isPrior = false;
        this.insuranceQuotes.unshift(props.termQuote);
      }
    }

    // all insurance quotes should point to the the current customer (this)
    this.insuranceQuotes.forEach((quote) => {
      quote.customer = this;
    });

    return this;
  }

  init() {
    super.init('CAS');
    return this;
  }

  clientInit() {
    super.clientInit();
  }

  initializeQuote(quoteType: string): void {
    if (!this.insuranceQuotes || this.insuranceQuotes.length === 0) {
      const quote = CoverpathCustomer.getDynamicQuoteObj(this, quoteType);
      if (quoteType === 'coverpathClientQuote') {
        quote.isAdvisorQuote = false;
      }
      this.insuranceQuotes.push(quote);
    }
  }

  get twoFactorAuth() {
    if (!this._twoFactorAuth) {
      this._twoFactorAuth = TwoFactorAuth.new();
    }
    return this._twoFactorAuth;
  }

  getTFAPhone() {
    return this.publicGetTFAPhone();
  }

  @remote({ on: 'server' })
  publicGetTFAPhone() {
    return this.twoFactorAuth.getPhone();
  }

  getTFAStatus() {
    return this.publicGetTFAStatus();
  }

  @remote({ on: 'server' })
  publicGetTFAStatus() {
    return this.twoFactorAuth.getStatus();
  }

  private getApplicationById(applicationId: string): CasApplication {
    return this.activeApplications.find((application) => {
      return application._id === applicationId;
    });
  }

  getLatestApplication(ignoreDraftsInProgress: boolean = false): CasApplication {
    //todo: this need more clean up - this should be changed to something else
    //https://massmutual.atlassian.net/browse/HL-19494
    let applications = this.activeApplications;
    if (ignoreDraftsInProgress) {
      applications = applications.filter(
        (app) => !app.isAgentAssistant || (app.isAgentAssistant && app.primaryPolicy.linkSentDate !== null)
      );
    }
    return applications.reduce(function (prev, current) {
      return Number(prev.applicationNumber) > Number(current.applicationNumber) ? prev : current;
    });
  }

  setCurrentApplication(applicationId?: string) {
    this._currentApplication = applicationId ? this.getApplicationById(applicationId) : this.getLatestApplication();
  }

  enrichCustomerOptimized(applicationId?: string) {
    const idMap = {};
    idMap[this._id] = this;

    const origChangeTracking = this.amorphic.__changeTracking__;
    this.amorphic.__changeTracking__ = false;

    const resetChangeTracking = () => {
      this.amorphic.__changeTracking__ = origChangeTracking;
      return true;
    };

    return super.enrichCustomerOptimized(applicationId).then(resetChangeTracking);
  }

  /**
   * This function was created by copying enrichCustomerOptimized function with these differences:
   * 1) unnecessary persistor fetches were removed, and only applications, policies and insureds are fetched.
   * 2) no current application is being set within.
   */
  @remote({ on: 'server' })
  async enrichCustomer() {
    const idMap = {};
    idMap[this._id] = this;

    const origChangeTracking = this.amorphic.__changeTracking__;
    this.amorphic.__changeTracking__ = false;

    const resetChangeTracking = () => {
      this.amorphic.__changeTracking__ = origChangeTracking;
      return true;
    };

    const retrieveApplications = () => {
      LoggerSingleton.getInstance().info({
        module: 'CoverpathCustomer.enrichCustomer',
        function: 'retrieveApplications',
        category: Category.request,
      });
      return this.fetch(
        {
          applications: {
            fetch: {
              policies: {
                fetch: {
                  insured: {
                    fetch: {
                      familyHistory: false,
                      personalHistory: false,
                      chronicIllnessRiderSupplement: false,
                      riskFactors: true,
                    },
                  },
                },
              },
            },
          },
        },
        null,
        idMap
      );
    };
    const retrieveApplicants = () => {
      LoggerSingleton.getInstance().info(
        {
          module: 'CoverpathCustomer.enrichCustomer',
          function: 'retrieveApplicants',

          category: Category.request,
        },
        'retrieveApplicants'
      );
      return this.fetch(
        {
          alternateCustomer: {
            fetch: {
              familyHistory: false,
              personalHistory: false,
              chronicIllnessRiderSupplement: false,
              riskFactors: false,
            },
          },
        },
        null,
        idMap
      );
    };

    await retrieveApplications();
    await retrieveApplicants();
    return resetChangeTracking();
  }

  @remote({ on: 'server' })
  async createPolicy(): Promise<void> {
    if (this.application && this.application.primaryPolicy && this.application.primaryPolicy.isSubmitting()) {
      throw new Error('Application in Progress - Cannot start a new one');
    }

    await this.createApplication();
    this.application.quote = this.primaryQuote;
    this.primaryQuote.application = this.application;
    this.createPrimaryPolicy();

    if (this.application.isJointApp()) {
      this.createSecondaryPolicy();
    }
  }

  private async createApplication() {
    if (!this.application) {
      this.application = CasApplication.new(this);
      await this.application.assignApplicationNumber();
    }
  }

  private createPrimaryPolicy() {
    if (!this.application.primaryPolicy) {
      const policy = this.newPolicy();
      this.application.addPrimaryPolicy(policy);
    }

    this.application.primaryPolicy.product = this.primaryQuote.product;
    this.application.primaryPolicy.productType = this.primaryQuote.productType;
    let quote: BaseQuote;
    if (this.primaryQuote.amorphicGetClassName() === CoverpathWholeLifeQuote.amorphicClassName) {
      quote = this.primaryQuote as CoverpathWholeLifeQuote;
    } else if (this.primaryQuote.amorphicGetClassName() === CoverpathTermQuote.amorphicClassName) {
      quote = this.primaryQuote as CoverpathTermQuote;
    } else {
      quote = this.primaryQuote as CoverpathAnnuityQuote;
    }

    (this.application.primaryPolicy as CoverpathPolicy).updateWithQuoteInfo(quote);
  }

  private createSecondaryPolicy() {
    if (this.termQuote.amorphicGetClassName() === CoverpathTermQuote.amorphicClassName) {
      const policy = this.newPolicy();
      this.application.addSecondaryPolicy(policy);
      this.application.secondaryPolicy.product = this.termQuote.product;
      this.application.secondaryPolicy.productType = this.termQuote.productType;
      (this.application.secondaryPolicy as CoverpathPolicy).updateWithQuoteInfo(this.application.quote.secondaryQuote);
    } else {
      UILoggerSingleton.getInstance().error(
        {
          module: 'Customer.createSecondaryPolicy',
          function: 'validate termQuote',
          category: UICategory.request,
          error: {
            isHumanRelated: false,
          },
        },
        `A Secondary Policy's quote should be type of CoverpathTermQuote, but it is actually ${this.termQuote.amorphicGetClassName()}`
      );
    }
  }

  newPolicy(): CoverpathPolicy {
    const policy = CoverpathPolicy.new(this);
    policy.product = this.primaryQuote.product;
    return policy;
  }

  @remote({ on: 'server' })
  async submitPolicy(enableConversionAutomation?: boolean) {
    // Make sure this is not a duplicate submission
    const policy = this.application.primaryPolicy;
    if (policy.submittedAt && policy.policyNumber) {
      return true;
    }

    const isEmailValidated = await this.isEmailValidated();

    if (!isEmailValidated) {
      throw new Error('Internal Error - email not validated');
    }
    try {
      const policyNumber = await policy.assignPolicyNumber();

      // Update progress, submit the policy to workflow and motor on.
      this.advanceApplicationProgress(Assumptions.progress.ApplicationSubmitted);
      policy.submittedAt = new Date();
      policy.submittedFromIP = this.amorphic['incomingIP'];

      LoggerSingleton.getInstance().info(
        {
          module: 'CoverpathCustomer.submitPolicy',
          function: 'createWorkflow',
          category: Category.request,
          data: {
            policyNumber: policyNumber,
          },
        },
        `Primary Policy number assigned`
      );
    } catch (err) {
      LoggerSingleton.getInstance().error(
        {
          module: 'CoverpathCustomer.submitPolicy',
          function: 'createWorkflow',

          category: Category.security,
          error: err,
        },
        `An error occured generating the policyNumber`
      );
      return err;
    }

    if (this.application.isJointApp()) {
      const secondaryPolicyNumber = await this.application.secondaryPolicy.assignPolicyNumber();

      LoggerSingleton.getInstance().info(
        {
          module: 'CoverpathCustomer.submitPolicy',
          function: 'createWorkflow',
          category: Category.request,
          data: {
            policyNumber: secondaryPolicyNumber,
          },
        },
        `Secondary Policy number assigned`
      );
    }

    /**
     * we need to handle the legitimate case of product throwing an error for missing rates
     * UW needs rates for all rate classes, which is done in `gatherRates()`
     * depending on age, smoker rates may not be available for a non-smoking policy
     * policy should continue on in this case
     *
     * TODO: HL-14580 `gatherRates()` should be removed to be handled by UW/ handle JA
     */
    try {
      await (policy as CoverpathPolicy).gatherRates();
    } catch (error) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'CoverpathCustomer.submitPolicy',
          function: 'gatherRates',
          category: Category.request,
        },
        `error with gathering rates for ${policy._id}`
      );
    }
    // If it is a conversion policy, start conversion workflow.
    if (policy.convertedFrom && enableConversionAutomation) {
      this.registerPostServerCallCallback(() => this.application.startConversionWorkflow());
    } else if (!policy.workflowState) {
      // If we don't have a workflow (Generic link) start one.
      this.registerPostServerCallCallback(() => this.application.startWorkflow('TE1 Check Tele CMI Requirements'));
    } else {
      // Else just advance the workflow already started.
      policy.fulfillReq('CS1R1 Application has been submitted');
    }
  }

  /**
   * Switch to a new application and policy, or a bare-bones one if none is passed.
   *
   * @remarks has some specific differences for Coverpath and Haven / MMD
   */
  public resetApplication(sourceApplication: CasApplication, newProgress, newPolicy?: CoverpathPolicy) {
    this.priorApplications.push(sourceApplication);

    if (typeof newProgress !== 'undefined') {
      this.resetApplicationProgress(newProgress);
    }

    return this.switchPolicy(sourceApplication, newPolicy);
  }

  /**
   * Switch to a new application and policy, or a bare-bones one if none is passed.
   * update quote and application reference
   * update insured
   *
   * @remarks switch policy for coverpath customer
   */
  @remote({ on: 'server' })
  async switchPolicy(sourceApplication: CasApplication, policyToSwitchTo?: CoverpathPolicy): Promise<void> {
    if (this.application.primaryPolicy.submittedAt && !this.application.primaryPolicy.toBeCanceledDeclinedRejected()) {
      throw new Error('Application Submitted or Submission in Progress - Cannot start a new one');
    }

    const primaryCustomer = this.primaryCustomer;
    const primaryCustomerPerson = primaryCustomer.person;
    const primaryAddress = primaryCustomer.address;
    const product = this.application.primaryPolicy.product;
    const primaryPolicyAgent = this.application.primaryPolicy.primaryPolicyAgent;
    const additionalPolicyAgents = this.application.primaryPolicy.additionalPolicyAgents;
    const quote = this.application.quote;
    const txn = this.amorphic.beginTransaction();
    const isTeleCMI = this.application.isTeleCMI;
    const skipTLIC = this.application.skipTLIC;
    const examOptOut = this.application.examOptOut;

    await this.markPriorApp(txn);
    await this.createApp(txn);
    await this.assignQuote(quote);
    await this.switchPol(policyToSwitchTo, primaryPolicyAgent, additionalPolicyAgents);

    // restore necessary primaryCustomer data
    this.primaryCustomer.person.firstName = primaryCustomerPerson.firstName;
    this.primaryCustomer.person.middleName = primaryCustomerPerson.middleName;
    this.primaryCustomer.person.lastName = primaryCustomerPerson.lastName;
    this.primaryCustomer.address = primaryAddress;
    this.updateInsuredEmailIfNeeded();

    // set 'isTelCMI' to what it was before application was reset
    if (isTeleCMI) {
      this.application.isTeleCMI = isTeleCMI;
    }

    this.application.skipTLIC = skipTLIC;
    this.application.examOptOut = examOptOut;

    return this.amorphic.commit({ transaction: txn });
  }

  private markPriorApp(txn) {
    // use a direct reference of the application so persistSave is called on the application itself.
    // if using this.application, only an active application will be returned and it will be undefined in this case
    // because the same application was just marked as prior.
    const application = this.application;
    application.type = ApplicationTypes.PRIOR;
    application.quote = null;
    return application.persistorSave({ transaction: txn });
  }

  private async createApp(txn) {
    this.application = CasApplication.new(this);
    await this.application.assignApplicationNumber();
    await this.application.applicationOriginator.persistorSave({ transaction: txn });
    return this.application.persistorSave({ transaction: txn });
  }

  private assignQuote(applicationQuote: BaseQuote) {
    this.application.quote = applicationQuote;
    applicationQuote.application = this.application;
  }

  private switchPol(policyToSwitchTo: Policy, primaryPolicyAgent: PolicyAgent, additionalPolicyAgents: PolicyAgent[]) {
    if (this.application) {
      this.reset();

      this.policies = [];
      if (policyToSwitchTo) {
        this.application.addPrimaryPolicy(policyToSwitchTo);
      } else {
        this.createPrimaryPolicy();
        const secondaryQuote = this.application.quote.secondaryQuote;
        if (this.application.isJointApp() || secondaryQuote) {
          this.createSecondaryPolicy();
        }

        // Set primary and split agents on the new application
        this.application.primaryPolicy.primaryPolicyAgent = PolicyAgent.new(
          this.application.primaryPolicy,
          primaryPolicyAgent.agent,
          primaryPolicyAgent.ratio,
          primaryPolicyAgent.renewalCommissionRatio,
          primaryPolicyAgent.agencyId,
          primaryPolicyAgent.corpId,
          primaryPolicyAgent.corpAgency
        );
        for (const additionalPolicyAgent of additionalPolicyAgents) {
          this.application.primaryPolicy.additionalPolicyAgents.push(
            PolicyAgent.new(
              this.application.primaryPolicy,
              additionalPolicyAgent.agent,
              additionalPolicyAgent.ratio,
              additionalPolicyAgent.renewalCommissionRatio,
              additionalPolicyAgent.agencyId,
              additionalPolicyAgent.corpId,
              additionalPolicyAgent.corpAgency
            )
          );
        }
      }
    }
  }

  protected async isEmailValidated(): Promise<boolean> {
    //Sanderling policies will not have emails & hence cannot be validated for submitting conversion policy
    if (this.channel === Product.channels.MMTRAD.code && this.application.primaryPolicy.convertedFrom) {
      return true;
    }

    //Agent Submitted Policies will not have email validated
    if (this.application.isAgentSubmittedApp) {
      return true;
    }

    // Legacy validation
    let isEmailValidated = this.emailValidated;

    if (!isEmailValidated) {
      const isAuth0Enabled = await Auth0Utils.isAuth0EnabledByProvider(AppConfigTypes.AUTH0_ENABLED_COVERPATH);

      if (isAuth0Enabled) {
        const accountInfo: AMCAuth0ResponseUser = await Auth0Client.getAMCUserInfo(this.email);
        isEmailValidated = accountInfo.email_verified;
      }
    }

    return isEmailValidated;
  }

  setPaidSource(url?: string) {
    const utm_source = Utils.getParameterByName('utm_source', url);

    if (utm_source || url) {
      this.paidSource = utm_source;
      this.paidSource_cid = Utils.getParameterByName('cid', url) || '';
      this.paidSource_pdata = Utils.getParameterByName('utm_medium', url) || '';
      this.paidSource_campaign = Utils.getParameterByName('utm_campaign', url) || '';
    }
  }

  async enrichApplication(applicationId: string): Promise<void> {
    const origChangeTracking = this.amorphic.__changeTracking__;
    this.amorphic.__changeTracking__ = false;

    LoggerSingleton.getInstance().info(
      {
        module: 'CoverpathCustomer',
        function: 'enrichApplication',
        category: Category.request,
      },
      `applicationId: ${applicationId}`
    );

    if (!applicationId) {
      throw new Error(`No applicationId provided`);
    }

    const application = await CasApplication.persistorFetchById(applicationId, {
      fetch: { customer: { fetch: { applications: false } } },
      session: this.amorphic,
    });

    if (!application) {
      throw new Error(`Cannot find application for applicationId ${applicationId}`);
    }

    await application.retrieveApplicant();
    await application.retrievePersonalHistory();

    this.amorphic.__changeTracking__ = origChangeTracking;
    this._currentApplication = application;
  }

  @remote({ on: 'server' })
  public async recordActiveApplications() {
    //record customer's current active applications on submit for analytics purposes
    const applicationsActive = this.applications.filter((application) => {
      const policy = application.primaryPolicy;
      if (application._id === this.application._id) {
        //current application
        return true;
      } else if (
        policy.submittedAt == null ||
        policy.workflowState == null ||
        CoverpathUtils.isPolicyCanceled(policy) ||
        CoverpathUtils.isPolicyInForce(policy)
      ) {
        return false;
      } else {
        return true;
      }
    });
    const activeAppIds = applicationsActive.map((app) => app._id);

    const customerActiveApps = CustomerActiveApplications.new(this._id, activeAppIds, this.application._id);
    await customerActiveApps.save();
    return customerActiveApps;
  }

  /**
   * to determine whether 2019 filing is enabled on Coverpath
   * when there is no application it does not matter - such as on client log out
   * this function exists when we cannot access the configs
   * can be removed once 2019 filing is released in all states
   */
  isCoverpath2019MetadataEnabled(policy?: Policy): boolean {
    if (!this.application && !policy) {
      return false;
    }

    const application = policy ? policy.application : this.application;
    const currentPolicy = application.primaryPolicy;
    const metadataVersion = currentPolicy.appMetadataVersion;
    const deprecatedMetadata2018Version = currentPolicy.isICCApplication()
      ? CoverpathUtils.deprecatedICCMetadata2018Version
      : CoverpathUtils.deprecatedNonICCMetadata2018Version;
    const channelCode = currentPolicy.getChannelCode();
    if (Utils.isCoverpathPlatform(channelCode) && metadataVersion > deprecatedMetadata2018Version) {
      return true;
    } else {
      return false;
    }
  }

  getCoverpathFilingType(policy?: Policy): string {
    const application = policy ? policy.application : this.application;
    const currentPolicy = application.primaryPolicy;
    const metadataVersion = currentPolicy.appMetadataVersion;
    const isNewYorkApplication = (currentPolicy as CoverpathPolicy).isNewYorkApplication();

    if (isNewYorkApplication || (currentPolicy as CoverpathPolicy).is2021CoverpathOrMMD()) {
      return CPMetadataFilingTypes.CP2021;
    } else if (metadataVersion > CoverpathUtils.deprecatedMetadata2019Version) {
      return CPMetadataFilingTypes.CP2020;
    } else if (this.isCoverpath2019MetadataEnabled(currentPolicy)) {
      return CPMetadataFilingTypes.CP2019;
    } else {
      return CPMetadataFilingTypes.CP2018;
    }
  }

  getDocuSign() {
    return CoverpathDocuSign;
  }
}
